import React, { useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { formatFigure } from 'Helpers/formatService';

import Tags from 'CommonComponents/Tags';
import Tooltip from 'CommonComponents/Tooltip';

import CircleCheckIcon from 'Assets/icons/circle-check.svg';
import EditIcon from 'Assets/icons/edit.svg';
import StarFilledIcon from 'Assets/icons/star-filled.svg';
import StarIcon from 'Assets/icons/star.svg';
import TrashIcon from 'Assets/icons/trash.svg';

export default function GeneralRisksTable(props) {
    const [risks, setRisks] = useState([]);
    const risksListRef = useRef();

    const columnsCount = useMemo(() => {
        const count = 20;
        if (props.showSourceColumn) {
            return count + 1;
        }
        if (props.impactCategories) {
            return count + props.impactCategories.length;
        }
        return count;
    }, [props.showSourceColumn, props.impactCategories]);

    useEffect(() => {
        setRisks(props.risks);
    }, [props.risks]);

    useEffect(() => {
        if (risksListRef.current) {
            risksListRef.current.scrollTop = risksListRef.current.scrollHeight;
        }
    }, [props.risks.length]);

    function handleOnDragEnd(result) {
        if (!result.destination) {
            return;
        }

        const newRisks = [...risks];
        const [reorderedRisks] = newRisks.splice(result.source.index, 1);
        newRisks.splice(result.destination.index, 0, reorderedRisks);
        setRisks(newRisks);

        axios.post(props.reorderUrl, {
            risks: newRisks.map((risk) => risk.id),
        });
    }

    return (
        <div className="inbud-table_wrapper">
            <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="risksList">
                    {(provided) => (
                        <div className={'tw-overflow-y-auto tw-max-h-[60vh]'} ref={provided.innerRef} {...provided.droppableProps}>
                            <table className="inbud-table tw-table-fixed risksList">
                                <thead>
                                    <tr>
                                        <th className="tw-sticky tw-top-0 tw-left-0 tw-bg-gray-100 tw-w-32 tw-z-10">{$t('Reference')}</th>
                                        {props.showSourceColumn && (
                                            <th className="tw-sticky tw-top-0 tw-left-32 tw-bg-gray-100 tw-w-64 tw-z-10">{$t('Source')}</th>
                                        )}
                                        <th
                                            className={classNames('tw-sticky tw-top-0 tw-bg-gray-100 tw-w-64 tw-z-10', {
                                                'tw-left-96': props.showSourceColumn,
                                                'tw-left-32': !props.showSourceColumn,
                                            })}
                                        >
                                            {$t('Department')}
                                        </th>
                                        <th className="tw-sticky tw-top-0 tw-w-96">{$t('Description')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Owner')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Status')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Type')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Impact')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Probability')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-16">{$t('Priority (RAG)')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Controlled Impact')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Controlled Probability')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-16">{$t('Residual Priority')}</th>
                                        {props.impactCategories?.map((impactCategory) => (
                                            <th key={impactCategory?.id} className="tw-sticky tw-top-0 tw-w-32">
                                                {impactCategory.title}
                                            </th>
                                        ))}
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('# Actions')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Actions - Completed')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Actions - Outstanding')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Budget Impact')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('AFC')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Value')}</th>
                                        <th className="tw-sticky tw-top-0 tw-w-32">{$t('Actions')}</th>
                                    </tr>
                                </thead>
                                {!props.isLoading && !!risks?.length && (
                                    <tbody>
                                        {risks.map((risk, index) => (
                                            <Draggable key={risk.id} draggableId={risk.id.toString()} index={index}>
                                                {(provided) => (
                                                    <GeneralRiskRow
                                                        key={risk.id}
                                                        showSourceColumn={props.showSourceColumn}
                                                        risk={risk}
                                                        impactCategories={props.impactCategories}
                                                        showUrl={props.showUrl}
                                                        onStar={props.onStar}
                                                        onEdit={props.onEdit}
                                                        onDelete={props.onDelete}
                                                        canManage={props.canManage}
                                                        provided={provided}
                                                    />
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </tbody>
                                )}
                                {props.isLoading && (
                                    <tbody>
                                        <tr>
                                            <td colSpan={columnsCount}>{$t('Loading')}...</td>
                                        </tr>
                                    </tbody>
                                )}
                                {!props.isLoading && !risks?.length && (
                                    <tbody>
                                        <tr>
                                            <td colSpan={columnsCount}>
                                                <span className="tw-italic">{$t('No Risks found')}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                )}
                            </table>
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </div>
    );
}

function GeneralRiskRow({ showSourceColumn, risk, impactCategories, showUrl, onStar, onEdit, onDelete, provided, ...props }) {
    const sourceNames = useMemo(() => {
        if (!risk.source_names) {
            return [$t('Central')];
        }
        try {
            return JSON.parse(risk.source_names);
        } catch (e) {
            console.error('Error parsing source names for risk ID ' + risk.id, e);
            return null;
        }
    }, [risk.source_names]);

    return (
        <tr ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
            <td className="tw-sticky tw-left-0 tw-bg-gray-100">{risk.id}</td>
            {showSourceColumn && (
                <td className="tw-sticky tw-left-32 tw-bg-gray-100">
                    <Tags tags={sourceNames} />
                </td>
            )}
            <td className={classNames('tw-sticky tw-bg-gray-100', { 'tw-left-96': showSourceColumn, 'tw-left-32': !showSourceColumn })}>
                {risk.budget_department_name}
            </td>
            <td>
                <Tooltip placement="auto" tooltipContent={risk.detailed_description ? risk.detailed_description : '-'} tooltipType="text">
                    <div className="tw-flex tw-space-x-1 tw-w-fit">
                        <div className="tw-text-blue">{risk.description}</div>
                    </div>
                </Tooltip>
            </td>
            <td>
                {risk.owner?.first_name} {risk.owner?.last_name}
            </td>
            <td>{risk.status?.title}</td>
            <td>{risk.type?.title}</td>
            <td>{risk.impact?.mark}</td>
            <td>{risk.probability?.mark}</td>
            <td style={{ backgroundColor: risk.classification_color }}></td>
            <td>{risk.controlled_impact?.mark}</td>
            <td>{risk.controlled_probability?.mark}</td>
            <td style={{ backgroundColor: risk.residual_classification_color }}></td>
            {impactCategories?.map((impactCategory) => (
                <td key={impactCategory?.id}>
                    <div className={'tw-flex tw-items-center tw-justify-center'}>
                        {risk.impact_category_ids?.includes(impactCategory?.id) && <CircleCheckIcon className="tw-w-5 tw-h-5 tw-text-green" />}
                    </div>
                </td>
            ))}
            <td className="inbud-numeric-cell">{formatFigure(risk.actions_count?.total, String)}</td>
            <td className="inbud-numeric-cell">{formatFigure(risk.actions_count?.completed, String)}</td>
            <td className="inbud-numeric-cell">{formatFigure(risk.actions_count?.outstanding, String)}</td>
            <td>{risk.budget_impact ? $t('Yes') : $t('No')}</td>
            <td>{risk.AFC ? $t('Yes') : $t('No')}</td>
            <td className="inbud-numeric-cell">{formatFigure(risk.amount)}</td>
            <td>
                <div className="tw-flex tw-items-center tw-gap-x-3">
                    <button type="button" onClick={() => onStar(risk)}>
                        {risk.is_hot ? <StarFilledIcon className="tw-w-3 tw-h-3 tw-text-blue" /> : <StarIcon className="tw-w-3 tw-h-3" />}
                    </button>
                    {!!props.canManage && (
                        <>
                            <button type="button" onClick={() => onEdit(risk)}>
                                <EditIcon className="tw-w-3 tw-h-3" />
                            </button>
                            <button type="button" className="tw-text-red" onClick={() => onDelete(risk)}>
                                <TrashIcon className="tw-w-3 tw-h-3" />
                            </button>
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
}
